import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { backendErrorMessage } from '@core/proposals/backend-error';
import { traceToText } from '@core/proposals/calculation-trace';
import { CalculationTraceService } from '@core/proposals/calculation-trace.service';
import { CalculationTrace } from '@core/proposals/calculation-trace.types';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Admin only "Calculo / Debug" panel (D18). The HOST template decides who sees it (`*ngIf="isAdmin"`), so a non
 * admin never instantiates it and therefore never triggers the query. Collapsed by default; every expand fetches
 * the trace again.
 */
@Component({
  selector: 'app-calculation-trace-panel',
  templateUrl: './calculation-trace-panel.component.html',
  styleUrls: ['./calculation-trace-panel.component.scss'],
})
export class CalculationTracePanelComponent implements OnChanges {
  @Input() kind: 'line' | 'offer' = 'line';
  @Input() id: number | null = null;

  expanded = false;
  loading = false;
  errorMessage: string | null = null;
  trace: CalculationTrace | null = null;
  copyState: 'idle' | 'copied' | 'failed' = 'idle';

  constructor(
    private readonly traceService: CalculationTraceService,
    private readonly translocoService: TranslocoService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] || changes['kind']) {
      this.trace = null;
      this.errorMessage = null;
      this.copyState = 'idle';
      if (this.expanded) {
        this.load();
      }
    }
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.load();
    }
  }

  private load(): void {
    if (this.id == null) {
      return;
    }
    this.loading = true;
    this.errorMessage = null;
    this.copyState = 'idle';
    const trace$ = this.kind === 'offer' ? this.traceService.getOfferTrace(this.id) : this.traceService.getLineTrace(this.id);
    trace$.subscribe({
      next: (trace) => {
        this.trace = trace;
        this.loading = false;
      },
      error: (err) => {
        this.trace = null;
        this.loading = false;
        this.errorMessage = backendErrorMessage(err) ?? this.translocoService.translate('trace.error');
      },
    });
  }

  get text(): string {
    return this.trace ? traceToText(this.trace) : '';
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.text);
      this.copyState = 'copied';
    } catch {
      // navigator.clipboard is undefined outside secure contexts (plain http) and can be denied by the browser.
      this.copyState = 'failed';
    }
  }
}
